import Config from 'services/config/config';
import callWebService, { callTypes } from 'services/utils/networks/network';

/** Convert DD-MM-YYYY (or already YYYY-MM-DD) to YYYY-MM-DD for API. */
const toApiDate = (value) => {
  if (!value || typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed || trimmed.toLowerCase() === 'invalid date') return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const match = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (match) return `${match[3]}-${match[2]}-${match[1]}`;
  return '';
};

/**
 * Lists sales targets.
 * Monthly: type=monthly, month, year, period_id, search
 * Weekly: type=weekly, period_id, week_start_from, week_start_to, search
 * Additional: type=additional, month, year, period_id, search
 */
export async function fetchSalesTargetsList(page = 1, limit = 10, filters = {}) {
  const params = new URLSearchParams({ page, limit });
  const listType = ['weekly', 'additional'].includes(filters.type) ? filters.type : 'monthly';
  params.append('type', listType);

  if (filters.period_id) params.append('period_id', String(filters.period_id));

  if (listType === 'weekly') {
    const weekFrom = toApiDate(filters.week_start_from);
    const weekTo = toApiDate(filters.week_start_to);
    if (weekFrom) params.append('week_start_from', weekFrom);
    if (weekTo) params.append('week_start_to', weekTo);
  } else if (!filters.period_id) {
    if (filters.month) params.append('month', filters.month);
    if (filters.year) params.append('year', filters.year);
  }

  if (filters.search) params.append('search', filters.search);

  const url = `${Config.preSalesList.salesTargetsList}?${params.toString()}`;
  const res = await callWebService(callTypes.get, {}, url);
  const payload = res?.data?.data;
  if (!payload) {
    return { results: [], total: 0 };
  }
  return {
    results: payload.results || [],
    total: payload.total ?? 0,
  };
}

const unwrapPeriodPayload = (res) => {
  const raw = res?.data;
  let payload = raw?.data ?? raw ?? null;
  if (payload && payload.data && !payload.id && !payload.period_month) {
    payload = payload.data;
  }
  if (Array.isArray(payload)) {
    return payload[0] ?? null;
  }
  return payload ?? null;
};

/**
 * Fetches the latest sales target period (includes weeks).
 */
export async function fetchLatestSalesTargetPeriod() {
  const url = Config.preSalesList.salesTargetsLatestPeriod;
  const res = await callWebService(callTypes.get, {}, url);
  return unwrapPeriodPayload(res);
}

/**
 * Lists target periods (no weeks) for filter dropdowns.
 * GET /api/sales-targets/target-periods
 */
export async function fetchSalesTargetPeriods(page = 1, limit = 50) {
  const params = new URLSearchParams({ page, limit });
  const url = `${Config.preSalesList.salesTargetsPeriods}?${params.toString()}`;
  const res = await callWebService(callTypes.get, {}, url);
  const payload = res?.data?.data ?? res?.data ?? null;
  if (!payload) return { results: [], total: 0 };

  if (Array.isArray(payload)) {
    return { results: payload, total: payload.length };
  }

  return {
    results: payload.results || payload.items || [],
    total: payload.total ?? payload.count ?? 0,
  };
}

/**
 * Fetches one target period by id (includes unique weeks[]).
 * GET /api/sales-targets/target-periods/:id
 */
export async function fetchSalesTargetPeriodById(id) {
  if (!id) return null;
  const url = `${Config.preSalesList.salesTargetsPeriods}/${id}`;
  const res = await callWebService(callTypes.get, {}, url);
  return unwrapPeriodPayload(res);
}

/**
 * Updates the end date of a sales target period.
 * @param {number|string} id - Period ID
 * @param {string} periodEnd - End date in YYYY-MM-DD format
 * @returns {Promise<Object>} API response
 */
export async function updateSalesTargetPeriodEnd(id, periodEnd) {
  if (!id || !periodEnd) {
    throw new Error('Both id and periodEnd are required');
  }
  const url = `${Config.preSalesList.salesTargetsUpdatePeriod}/${id}`;
  const res = await callWebService(callTypes.put, { period_end: periodEnd }, url);
  return res?.data;
}

/**
 * Closes the current period with the given end date and creates the next period.
 * POST /api/sales-targets/target-periods/close-and-create-next
 * @param {string} periodEnd - End date in YYYY-MM-DD format (e.g. "2026-03-31")
 * @returns {Promise<Object>} API response
 */
export async function closeAndCreateNextPeriod(periodEnd) {
  if (!periodEnd) {
    throw new Error('periodEnd is required');
  }
  const url = Config.preSalesList.salesTargetsCloseAndCreateNext;
  const res = await callWebService(callTypes.post, { period_end: periodEnd }, url);
  return res?.data;
}

/**
 * Error thrown by set-targets / set-weekly-targets when the API rejects the CSV.
 * Carries an optional `rowErrors` array with per-row details.
 */
export class SalesTargetsApiError extends Error {
  constructor(message, rowErrors = []) {
    super(message);
    this.name = 'SalesTargetsApiError';
    this.rowErrors = rowErrors;
  }
}

/**
 * True when callWebService returned an HTTP/business error payload
 * (POST catch returns error.response instead of throwing).
 */
const isSalesTargetsErrorResponse = (res) => {
  if (!res) return true;
  const httpStatus = res.status;
  if (typeof httpStatus === 'number' && httpStatus >= 400) return true;
  const body = res.data;
  if (!body) return true;
  if (body?.response?.status === false) return true;
  if (Number(body?.status) === 1) return true;
  return false;
};

const throwSalesTargetsError = (res) => {
  const body = res?.data;
  const message =
    body?.message ||
    body?.error ||
    body?.response?.message ||
    'Something went wrong. Please try again.';
  const rowErrors = Array.isArray(body?.errors) ? body.errors : [];
  throw new SalesTargetsApiError(message, rowErrors);
};

/**
 * Sets sales targets from CSV URL for a specific month & year.
 * @param {Object} payload - { csv_url: string, target_start_date: string (YYYY-MM-DD) }
 * @returns {Promise<Object>} API response
 */
export async function setSalesTargets(payload) {
  const csvUrl = payload?.csv_url;
  const targetStartDate = payload?.target_start_date;
  if (!csvUrl || typeof csvUrl !== 'string' || !csvUrl.trim()) {
    throw new Error('csv_url is required');
  }
  if (!targetStartDate || typeof targetStartDate !== 'string' || !targetStartDate.trim()) {
    throw new Error('target_start_date is required');
  }
  const url = Config.preSalesList.salesTargetsSetTargets;
  const res = await callWebService(
    callTypes.post,
    { csv_url: csvUrl.trim(), target_start_date: targetStartDate.trim() },
    url
  );
  if (isSalesTargetsErrorResponse(res)) {
    throwSalesTargetsError(res);
  }
  return res?.data;
}

/**
 * Sets weekly sales targets from CSV URL for the current month.
 * POST /api/sales-targets/set-weekly-targets
 * @param {Object} payload - { csv_url: string }
 * @returns {Promise<Object>} API response
 */
export async function setWeeklySalesTargets(payload) {
  const csvUrl = payload?.csv_url;
  if (!csvUrl || typeof csvUrl !== 'string' || !csvUrl.trim()) {
    throw new Error('csv_url is required');
  }
  const url = Config.preSalesList.salesTargetsSetWeeklyTargets;
  const res = await callWebService(callTypes.post, { csv_url: csvUrl.trim() }, url);
  if (isSalesTargetsErrorResponse(res)) {
    throwSalesTargetsError(res);
  }
  return res?.data;
}

/**
 * Uploads additional achievements from CSV URL (sales coordinator).
 * POST /api/sales-targets/upload-additional-achievements
 * @param {Object} payload - { csv_url: string }
 * @returns {Promise<Object>} API response with created, updated, errors
 */
export async function uploadAdditionalAchievements(payload) {
  const csvUrl = payload?.csv_url;
  if (!csvUrl || typeof csvUrl !== 'string' || !csvUrl.trim()) {
    throw new Error('csv_url is required');
  }
  const url = Config.preSalesList.salesTargetsUploadAdditionalAchievements;
  const res = await callWebService(callTypes.post, { csv_url: csvUrl.trim() }, url);
  if (isSalesTargetsErrorResponse(res)) {
    throwSalesTargetsError(res);
  }
  return res?.data;
}
